import React, { useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import {
  MapContainer,
  Brand,
  WeatherLayerType,
  type MapUnits,
  type FeatureFlag,
} from '@surfline/map-lib';
import '@surfline/map-lib/styles.css'; // Global styles
import { useUserUnits, useUserPermissionStatus } from 'selectors/user';
import useMapSettings from '../../hooks/useMapSettings';
import { MapSettings } from 'types/map';
import styles from './Surftiler.module.scss';
import config, { env } from '../../config';
import { getAccessToken } from 'common/baseFetch';
import MetaTags from 'components/MetaTags';
import { BW_IOS_OCEAN_CURRENTS_LAYER, BW_IOS_WIND_AND_CURRENTS_LAYER } from 'common/treatments';

interface SurftilerProps {
  hasSubnav?: boolean;
  noHeader?: boolean;
  featureFlags?: FeatureFlag[];
  metaData?: {
    pageTitle: string;
    url: string;
    metaDescription: string;
  };
}

const Surftiler: React.FC<SurftilerProps> = ({
  hasSubnav = false,
  noHeader = false,
  featureFlags = [],
  metaData,
}) => {
  const userUnits = useUserUnits();
  const { mapSettings, updateMapSettings } = useMapSettings();

  const {
    hasCoreForecastPermissions: originalHasCoreForecastPermissions,
    hasCustomForecastPermissions,
    hasHistoricForecastPermissions,
    hasExtendedChartLayerPermissions,
  } = useUserPermissionStatus();

  const router = useRouter();

  // Check for client_id in query parameters
  const clientId = useMemo(() => {
    return (router.query.client_id as string) || null;
  }, [router.query.client_id]);

  // Override hasCoreForecastPermissions if client_id matches
  const hasCoreForecastPermissions = useMemo(() => {
    if (clientId === '6123ef4f5192b46d74d575a6') {
      return true;
    }
    return originalHasCoreForecastPermissions;
  }, [clientId, originalHasCoreForecastPermissions]);

  const permissionKeys = useMemo(() => {
    const perms: string[] = [];
    if (hasCoreForecastPermissions) perms.push('CORE_FORECAST');
    if (hasCustomForecastPermissions) perms.push('CUSTOM_FORECAST');
    if (hasHistoricForecastPermissions) perms.push('HISTORIC_FORECAST');
    if (hasExtendedChartLayerPermissions) perms.push('EXTENDED_CHART_LAYER');
    return perms;
  }, [
    hasCoreForecastPermissions,
    hasCustomForecastPermissions,
    hasHistoricForecastPermissions,
    hasExtendedChartLayerPermissions,
  ]);

  const handleMapSettingsChange = useCallback(
    (newMapSettings: MapSettings) => {
      updateMapSettings(newMapSettings);
    },
    [updateMapSettings],
  );

  const containerClassName = useMemo(() => {
    if (noHeader) {
      return styles.maplibContainerNoHeader;
    }
    return hasSubnav ? styles.maplibContainerWithSubnav : styles.maplibContainer;
  }, [hasSubnav, noHeader]);

  const accessToken = getAccessToken();

  const mapReactKey = useMemo(() => {
    return env === 'development' ? `map-${Date.now()}` : undefined;
  }, []);

  const layerTypes = useMemo(() => {
    const types: WeatherLayerType[] = [];
    if (featureFlags.some((f) => f.key === BW_IOS_OCEAN_CURRENTS_LAYER && f.value === 'on'))
      types.push(WeatherLayerType.CURRENTS);
    if (featureFlags.some((f) => f.key === BW_IOS_WIND_AND_CURRENTS_LAYER && f.value === 'on'))
      types.push(WeatherLayerType.WIND_AND_CURRENTS);
    return types;
  }, [featureFlags]);

  return (
    <div className={containerClassName}>
      {metaData && (
        <MetaTags
          title={metaData.pageTitle}
          description={metaData.metaDescription}
          urlPath={metaData.url}
        />
      )}
      <MapContainer
        // Key fixes hot reload issue causing apiHostname to be reset.
        key={mapReactKey}
        maptilerApiKey={config.mapTiler.key}
        cdnHostname={config.charts.hostname}
        brand={Brand.SURFLINE}
        units={userUnits as MapUnits}
        permissionKeys={permissionKeys}
        apiHostname={config.servicesURL}
        accessToken={accessToken}
        // @ts-ignore - Type mismatch between local and map-lib MapSettings types
        mapSettings={mapSettings}
        // @ts-ignore - Type mismatch between local and map-lib MapSettings types
        onMapSettingsChange={handleMapSettingsChange}
        featureFlags={featureFlags}
        layerTypes={layerTypes}
        hideTooltipOnEmbed={true}
      />
    </div>
  );
};

export default Surftiler;
